/**
 * Rooms — a purely-visual primitive the reducer stores opaquely.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import { encounterRooms, type EncounterState } from "../types";
import { changed, type EncounterHandler } from "./context";

export const applySetRoom: EncounterHandler<"set_room"> = (state, cmd) => {
  // Replace the room at `room.id` wholesale (create / union-extend / rename all flow
  // through this one command — the new rect list / name is built at the call site).
  // Rooms are PURELY VISUAL: the reducer stores the blob opaquely —
  // it never reads a rect's coords, branches on grid mode, or folds into any
  // movement/sight seam. The inverse is SYMMETRIC: a prior room at that id existed →
  // set_room restoring it; it was new → remove_room (round-trip exact).
  const rooms = encounterRooms(state);
  const prev = rooms[cmd.room.id];
  const next: EncounterState = {
    ...state,
    rooms: { ...rooms, [cmd.room.id]: cmd.room },
  };
  return ok({
    state: next,
    inverse: prev
      ? { type: "set_room", actorId: cmd.actorId, room: prev }
      : { type: "remove_room", actorId: cmd.actorId, roomId: cmd.room.id },
    events: changed(null),
  });
};

export const applyRemoveRoom: EncounterHandler<"remove_room"> = (state, cmd) => {
  // Reject an unknown room id (a sanity check, like remove_combatant). The inverse is a
  // set_room re-adding the captured room (round-trip exact). Purely visual — no fallout.
  const rooms = encounterRooms(state);
  const removed = rooms[cmd.roomId];
  if (!removed) {
    return err(new CommandError(`remove_room: unknown room ${cmd.roomId}`));
  }
  const { [cmd.roomId]: _gone, ...rest } = rooms;
  const next: EncounterState = { ...state, rooms: rest };
  return ok({
    state: next,
    inverse: { type: "set_room", actorId: cmd.actorId, room: removed },
    events: changed(null),
  });
};
